/**
 * Gift Form Backup Properties
 * Adds backup gift data as visible line item properties in real-time
 * Completely separate from persistence logic
 */

(function() {
  'use strict';

  // Create and maintain hidden inputs in the product form
  function syncGiftPropertiesToForm() {
    // Find the <product-form> custom element, then get the <form> inside it
    const productFormElement = document.querySelector('product-form');
    if (!productFormElement) {
      console.log('[Backup Props] <product-form> element not found');
      // Try to find any product-form elements
      const allProductForms = document.querySelectorAll('product-form');
      console.log('[Backup Props] Found product-form elements:', allProductForms.length);
      return;
    }

    console.log('[Backup Props] Found <product-form> element:', productFormElement);

    const productForm = productFormElement.querySelector('form[id^="product-form-"]');
    if (!productForm) {
      console.log('[Backup Props] <form> inside <product-form> not found');
      // Try to find any forms inside
      const allForms = productFormElement.querySelectorAll('form');
      console.log('[Backup Props] Forms inside <product-form>:', allForms.length);
      if (allForms.length > 0) {
        console.log('[Backup Props] First form ID:', allForms[0].id);
      }
      return;
    }

    console.log('[Backup Props] Found product form:', productForm.id);
    console.log('[Backup Props] Form parent:', productForm.parentElement?.tagName);

    // Get gift form values
    const firstName = document.getElementById('rc_gift_recipient_first_name')?.value || '';
    const lastName = document.getElementById('rc_gift_recipient_last_name')?.value || '';
    const email = document.getElementById('rc_gift_recipient_email')?.value || '';
    const message = document.getElementById('rc_gift_message')?.value || '';
    const scheduledAt = document.getElementById('rc_gift_notification_scheduled_at')?.value || '';

    console.log('[Backup Props] Gift values:', { firstName, lastName, email, message, scheduledAt });

    // Only proceed if required fields are filled
    if (!firstName || !lastName || !email) {
      console.log('[Backup Props] Required fields not filled, removing properties');
      // Remove properties if they exist
      productForm.querySelectorAll('input[data-gift-backup-property]').forEach(input => input.remove());
      return;
    }

    console.log('[Backup Props] Creating/updating properties');

    // Helper to create or update a property input
    function setProperty(name, value) {
      // Only look for OUR properties (with the data attribute) - never touch Recharge properties
      const existingInput = productForm.querySelector(`input[name="properties[${name}]"][data-gift-backup-property]`);

      if (value) {
        if (existingInput) {
          // Update existing backup property
          existingInput.value = value;
          console.log(`[Backup Props] Updated: ${name} = ${value}`);
        } else {
          // Create new backup property
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${name}]`;
          input.value = value;
          input.setAttribute('data-gift-backup-property', 'true');

          // Insert after the last backup property if exists, otherwise after last Recharge property
          const backupProps = productForm.querySelectorAll('input[data-gift-backup-property]');
          if (backupProps.length > 0) {
            // Insert after the last backup property
            const lastBackupProp = backupProps[backupProps.length - 1];
            lastBackupProp.parentNode.insertBefore(input, lastBackupProp.nextSibling);
          } else {
            // Insert after the last Recharge property
            const rechargeProps = productForm.querySelectorAll('input[name^="properties[__rc_"]');
            if (rechargeProps.length > 0) {
              const lastRechargeProp = rechargeProps[rechargeProps.length - 1];
              lastRechargeProp.parentNode.insertBefore(input, lastRechargeProp.nextSibling);
            } else {
              productForm.appendChild(input);
            }
          }

          console.log(`[Backup Props] Created: ${name} = ${value}`);
        }
      } else if (existingInput) {
        // Remove backup property if value is empty (but never remove Recharge properties)
        existingInput.remove();
        console.log(`[Backup Props] Removed: ${name}`);
      }
    }

    // First, ensure Recharge properties exist in correct order
    // Remove any existing Recharge properties first
    productForm.querySelectorAll('input[name^="properties[__rc_gift"]').forEach(input => input.remove());

    // Helper to add Recharge property at the beginning of the form
    function addRechargeProperty(name, value) {
      if (value !== undefined && value !== null && value !== '') {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = `properties[${name}]`;
        input.value = value;
        productForm.insertBefore(input, productForm.firstChild);
        console.log(`[Backup Props] Added Recharge property: ${name} = ${value}`);
      }
    }

    // Add Recharge properties in reverse order (since we're inserting at the beginning)
    // This will result in: First Name, Last Name, Email, Note, Scheduled At
    if (scheduledAt) {
      addRechargeProperty('__rc_gift_notification_scheduled_at', scheduledAt);
    }
    addRechargeProperty('__rc_gift_recipient_note', message);
    addRechargeProperty('__rc_gift_recipient_email', email);
    addRechargeProperty('__rc_gift_recipient_last_name', lastName);
    addRechargeProperty('__rc_gift_recipient_first_name', firstName);

    // Then set our backup properties with customer-friendly names in correct order
    setProperty('Gift Recipient', `${firstName} ${lastName}`);
    setProperty('Gift Recipient Email', email);
    if (message) {
      setProperty('Gift Message', message);
    }
    if (scheduledAt) {
      setProperty('Gift Delivery Date', scheduledAt);
    }

    console.log('[Backup Props] All properties synced to form');
  }

  // Set up listeners on gift form fields
  function setupListeners() {
    const fieldIds = [
      'rc_gift_recipient_first_name',
      'rc_gift_recipient_last_name',
      'rc_gift_recipient_email',
      'rc_gift_message',
      'rc_gift_notification_scheduled_at'
    ];

    console.log('[Backup Props] Setting up field listeners');

    fieldIds.forEach(id => {
      const input = document.getElementById(id);
      if (input) {
        console.log('[Backup Props] Adding listener to:', id);
        input.addEventListener('input', syncGiftPropertiesToForm);
        input.addEventListener('change', syncGiftPropertiesToForm);
        input.addEventListener('blur', syncGiftPropertiesToForm);
      }
    });

    // Initial sync
    syncGiftPropertiesToForm();
  }

  // Wait for gift form to appear
  function waitForGiftForm() {
    const giftForm = document.querySelector('form[id*="rc_gifting"]');

    if (giftForm) {
      console.log('[Backup Props] Gift form found, setting up listeners');
      setupListeners();
      return;
    }

    console.log('[Backup Props] Gift form not found, waiting...');

    // Use observer to wait
    const observer = new MutationObserver((mutations) => {
      const relevantChange = mutations.some(mutation => {
        return Array.from(mutation.addedNodes).some(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            return node.matches?.('form[id*="rc_gifting"]') ||
                   node.querySelector?.('form[id*="rc_gifting"]');
          }
        });
      });

      if (relevantChange) {
        console.log('[Backup Props] Gift form appeared via observer');
        observer.disconnect();
        setTimeout(setupListeners, 500);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    setTimeout(() => observer.disconnect(), 10000);
  }

  // Initialize
  console.log('[Backup Props] Initializing...');

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      console.log('[Backup Props] DOM ready');
      setTimeout(waitForGiftForm, 1000);
    });
  } else {
    console.log('[Backup Props] DOM already ready');
    setTimeout(waitForGiftForm, 1000);
  }

  // Re-initialize after AJAX
  window.addEventListener('product:loaded', () => {
    console.log('[Backup Props] Product loaded event, reinitializing');
    setTimeout(waitForGiftForm, 600);
  });

})();
